import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import dailyIntakeService from "../../services/dailyIntakeService";
import patientService from "../../services/patientService";
import { useResolvedPatientId } from "../../hooks/useResolvedPatientId";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Slider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlGroup,
  Radio,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  //Card,
  //CardContent,
  //Divider,
  Rating,
  //LinearProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
//import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import MoodIcon from "@mui/icons-material/Mood";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import WarningIcon from "@mui/icons-material/Warning";

const DailyIntakeForm = () => {
  const { patientId, loading: resolvingPatientId, error: patientIdError } = useResolvedPatientId();

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [patientName, setPatientName] = useState("");
  const [consecutiveDays, setConsecutiveDays] = useState(0);

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],

    // Vital Signs
    weight: "",
    bloodPressureSystolic: "",
    bloodPressureDiastolic: "",
    heartRate: "",
    temperature: "",
    oxygenSaturation: "",

    // Symptoms (0-10 scale)
    fatigue: 0,
    shortnessOfBreath: 0,
    chestPain: 0,
    swelling: 0,
    nausea: 0,
    dizziness: 0,
    jointPain: 0,
    numbnessTingling: 0,

    // Quality of Life
    overallWellbeing: 3,
    moodRating: 3,
    sleepQuality: 3,
    appetiteLevel: 3,
    activityLevel: "moderate",

    // Medication Adherence
    medicationsTaken: true,
    missedMedications: "",

    // Activities
    exerciseMinutes: 0,
    walkingDistance: "",
    mealsEaten: 3,
    waterIntake: 8,

    // Adverse Events
    newSymptoms: false,
    newSymptomsDescription: "",
    fellToday: false,
    hospitalVisit: false,
    emergencyRoom: false,

    // Notes
    additionalNotes: "",
  });

  const loadPatientInfo = useCallback(async () => {
    if (!patientId) return;
    try {
      const response = await patientService.getPatientById(patientId);
      if (response.success) {
        const data = response.data;
        setPatientName(`${data.firstName} ${data.lastName}`);
      }
    } catch (err) {
      console.error("Error loading patient:", err);
    }
  }, [patientId]);

  const loadStreak = useCallback(async () => {
    if (!patientId) return;
    try {
      const response = await dailyIntakeService.getStreak(patientId);
      if (response.success) {
        setConsecutiveDays(response.data.consecutiveDays || 0);
      }
    } catch (err) {
      console.error("Error loading streak:", err);
    }
  }, [patientId]);

  useEffect(() => {
    loadPatientInfo();
    loadStreak();
  }, [loadPatientInfo, loadStreak]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!patientId) {
      setError("Your patient profile hasn't loaded yet. Please wait a moment and try again.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await dailyIntakeService.createIntake(
        patientId,
        formData
      );

      if (response.success) {
        setSuccess(true);
        setConsecutiveDays((prev) => prev + 1);

        setTimeout(() => {
          navigate(`/patients/${patientId}/daily-intake/history`);
        }, 2000);
      } else if (response.fieldErrors) {
        // Specific per-field messages from the backend's range validation
        // (e.g. "bloodPressureSystolic: Systolic BP must be at most 300
        // mmHg") — more useful than the generic message above it.
        const details = Object.values(response.fieldErrors).join(" ");
        setError(details || response.message || "Failed to save daily intake. Please try again.");
      } else {
        setError(
          response.message || "Failed to save daily intake. Please try again."
        );
      }
    } catch (err) {
      console.error("Error saving intake:", err);
      setError("Failed to save daily intake. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getSymptomColor = (value) => {
    if (value <= 3) return "success";
    if (value <= 6) return "warning";
    return "error";
  };

  // Don't render the form (or fire any data-fetching) until we have a real,
  // resolved patient ID — this is what actually fixes the patient_id=1 bug,
  // rather than letting the form render immediately with an unresolved value.
  if (resolvingPatientId) {
    return (
      <Container maxWidth="lg" sx={{ mt: 8, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (patientIdError || !patientId) {
    return (
      <Container maxWidth="lg" sx={{ mt: 8 }}>
        <Alert severity="error">{patientIdError || "Could not load your patient profile."}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
          color: "white",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Daily Health Log
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, mt: 1 }}>
              {patientName}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
              {new Date(formData.date).toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Chip
              icon={<CalendarTodayIcon />}
              label={`${consecutiveDays} Day Streak`}
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "white",
                fontWeight: "bold",
                fontSize: "1rem",
                py: 2,
                px: 1,
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Alerts */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Daily intake saved successfully! Great job staying consistent! 🎉
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        {/* Vital Signs Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <LocalHospitalIcon color="primary" />
            <Typography variant="h5" fontWeight="bold" color="primary">
              Vital Signs
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Weight (lbs)"
                type="number"
                value={formData.weight}
                onChange={(e) => handleChange("weight", e.target.value)}
                InputProps={{
                  inputProps: { min: 20, max: 700, step: 0.1 },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  label="BP Systolic"
                  type="number"
                  value={formData.bloodPressureSystolic}
                  onChange={(e) =>
                    handleChange("bloodPressureSystolic", e.target.value)
                  }
                  InputProps={{ inputProps: { min: 40, max: 300 } }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="BP Diastolic"
                  type="number"
                  value={formData.bloodPressureDiastolic}
                  onChange={(e) =>
                    handleChange("bloodPressureDiastolic", e.target.value)
                  }
                  InputProps={{ inputProps: { min: 20, max: 200 } }}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Heart Rate (bpm)"
                type="number"
                value={formData.heartRate}
                onChange={(e) => handleChange("heartRate", e.target.value)}
                InputProps={{ inputProps: { min: 20, max: 250 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Temperature (°F)"
                type="number"
                value={formData.temperature}
                onChange={(e) => handleChange("temperature", e.target.value)}
                InputProps={{ inputProps: { min: 85, max: 115, step: 0.1 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Oxygen Saturation (%)"
                type="number"
                value={formData.oxygenSaturation}
                onChange={(e) =>
                  handleChange("oxygenSaturation", e.target.value)
                }
                InputProps={{ inputProps: { min: 0, max: 100 } }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Symptoms Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <FavoriteIcon color="primary" />
            <Typography variant="h5" fontWeight="bold" color="primary">
              Symptom Severity (0 = None, 10 = Severe)
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {[
              { key: "fatigue", label: "Fatigue / Tiredness" },
              { key: "shortnessOfBreath", label: "Shortness of Breath" },
              { key: "chestPain", label: "Chest Pain / Discomfort" },
              { key: "swelling", label: "Swelling (Legs, Ankles, Feet)" },
              { key: "nausea", label: "Nausea / Upset Stomach" },
              { key: "dizziness", label: "Dizziness / Lightheadedness" },
              { key: "jointPain", label: "Joint Pain / Stiffness" },
              { key: "numbnessTingling", label: "Numbness / Tingling" },
            ].map(({ key, label }) => (
              <Grid item xs={12} key={key}>
                <FormControl fullWidth>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <FormLabel>{label}</FormLabel>
                    <Chip
                      label={formData[key]}
                      color={getSymptomColor(formData[key])}
                      size="small"
                    />
                  </Box>
                  <Slider
                    value={formData[key]}
                    onChange={(e, value) => handleChange(key, value)}
                    min={0}
                    max={10}
                    step={1}
                    marks
                    valueLabelDisplay="auto"
                    color={getSymptomColor(formData[key])}
                  />
                </FormControl>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Quality of Life Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <MoodIcon color="primary" />
            <Typography variant="h5" fontWeight="bold" color="primary">
              Quality of Life
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel>Overall Well-being</FormLabel>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}
                >
                  <Rating
                    value={formData.overallWellbeing}
                    onChange={(e, value) =>
                      handleChange("overallWellbeing", value)
                    }
                    max={5}
                    size="large"
                  />
                  <Typography>({formData.overallWellbeing}/5)</Typography>
                </Box>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel>Mood Rating</FormLabel>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}
                >
                  <Rating
                    value={formData.moodRating}
                    onChange={(e, value) => handleChange("moodRating", value)}
                    max={5}
                    size="large"
                  />
                  <Typography>({formData.moodRating}/5)</Typography>
                </Box>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <BedtimeIcon fontSize="small" /> Sleep Quality
                </FormLabel>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}
                >
                  <Rating
                    value={formData.sleepQuality}
                    onChange={(e, value) => handleChange("sleepQuality", value)}
                    max={5}
                    size="large"
                  />
                  <Typography>({formData.sleepQuality}/5)</Typography>
                </Box>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <RestaurantIcon fontSize="small" /> Appetite Level
                </FormLabel>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}
                >
                  <Rating
                    value={formData.appetiteLevel}
                    onChange={(e, value) =>
                      handleChange("appetiteLevel", value)
                    }
                    max={5}
                    size="large"
                  />
                  <Typography>({formData.appetiteLevel}/5)</Typography>
                </Box>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel>Activity Level Today</FormLabel>
                <RadioGroup
                  row
                  value={formData.activityLevel}
                  onChange={(e) =>
                    handleChange("activityLevel", e.target.value)
                  }
                  sx={{ mt: 1 }}
                >
                  <FormControlLabel
                    value="sedentary"
                    control={<Radio />}
                    label="Sedentary"
                  />
                  <FormControlLabel
                    value="light"
                    control={<Radio />}
                    label="Light"
                  />
                  <FormControlLabel
                    value="moderate"
                    control={<Radio />}
                    label="Moderate"
                  />
                  <FormControlLabel
                    value="active"
                    control={<Radio />}
                    label="Active"
                  />
                  <FormControlLabel
                    value="veryActive"
                    control={<Radio />}
                    label="Very Active"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Medication & Activities Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <FitnessCenterIcon color="primary" />
            <Typography variant="h5" fontWeight="bold" color="primary">
              Medications & Daily Activities
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.medicationsTaken}
                    onChange={(e) =>
                      handleChange("medicationsTaken", e.target.checked)
                    }
                    color="primary"
                  />
                }
                label="I took all my prescribed medications today"
              />
            </Grid>

            {!formData.medicationsTaken && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Which medications did you miss?"
                  value={formData.missedMedications}
                  onChange={(e) =>
                    handleChange("missedMedications", e.target.value)
                  }
                  multiline
                  rows={2}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Exercise (minutes)"
                type="number"
                value={formData.exerciseMinutes}
                onChange={(e) =>
                  handleChange("exerciseMinutes", e.target.value)
                }
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Walking Distance"
                placeholder="e.g., 2 miles (optional)"
                value={formData.walkingDistance}
                onChange={(e) =>
                  handleChange("walkingDistance", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Meals Eaten"
                type="number"
                value={formData.mealsEaten}
                onChange={(e) => handleChange("mealsEaten", e.target.value)}
                InputProps={{ inputProps: { min: 0, max: 10 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Water (8oz glasses)"
                type="number"
                value={formData.waterIntake}
                onChange={(e) => handleChange("waterIntake", e.target.value)}
                InputProps={{ inputProps: { min: 0, max: 20 } }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Adverse Events Section */}
        <Paper
          elevation={2}
          sx={{ p: 3, mb: 3, borderLeft: 4, borderColor: "warning.main" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <WarningIcon color="warning" />
            <Typography variant="h5" fontWeight="bold" color="warning.main">
              Adverse Events & New Symptoms
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.newSymptoms}
                    onChange={(e) =>
                      handleChange("newSymptoms", e.target.checked)
                    }
                    color="warning"
                  />
                }
                label="I experienced new or worsening symptoms today"
              />
            </Grid>

            {formData.newSymptoms && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Please describe the new symptoms"
                  value={formData.newSymptomsDescription}
                  onChange={(e) =>
                    handleChange("newSymptomsDescription", e.target.value)
                  }
                  multiline
                  rows={3}
                  placeholder="Describe what you experienced, when it started, and how severe it was..."
                />
              </Grid>
            )}

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.fellToday}
                    onChange={(e) =>
                      handleChange("fellToday", e.target.checked)
                    }
                    color="warning"
                  />
                }
                label="I fell or lost balance today"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.hospitalVisit}
                    onChange={(e) =>
                      handleChange("hospitalVisit", e.target.checked)
                    }
                    color="warning"
                  />
                }
                label="Hospital/clinic visit today"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.emergencyRoom}
                    onChange={(e) =>
                      handleChange("emergencyRoom", e.target.checked)
                    }
                    color="error"
                  />
                }
                label="Emergency room visit"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Additional Notes Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Additional Notes (Optional)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={formData.additionalNotes}
            onChange={(e) => handleChange("additionalNotes", e.target.value)}
            placeholder="Any other information you'd like to record about today..."
          />
        </Paper>

        {/* Submit Button */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate(`/patients/profile/${patientId}`)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={loading || success}
            sx={{ px: 4 }}
          >
            {loading ? "Saving..." : "Save Daily Log"}
          </Button>
        </Box>
      </form>
    </Container>
  );
};

export default DailyIntakeForm;